import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useData, useDrivers } from "../store/dataStore";
import { useDrill } from "../store/drillStore";
import { runDrill, measure } from "../query/runDrill";
import { ruleOf40 } from "../query/metrics";
import { fmtMoney, fmtPct, fmtNum } from "../query/format";
import { KpiCard } from "../components/KpiCard";
import { Drillable } from "../components/Drillable";
import { DriverCell } from "../components/DriverCell";

function computePnl(tables, planOverrides) {
  const gl = tables.fact_gl || [];
    const plan = tables.plan || [];
    const rev = measure(gl, "SUM(amount)", { account_type: "revenue" });
    const opex = measure(gl, "SUM(amount)", { account_type: "opex" });
    const revByMonth = runDrill(gl, { measure: "SUM(amount)", groupBy: ["period"], filters: { account_type: "revenue" }, orderBy: "dim" });
    const opexByMonth = runDrill(gl, { measure: "SUM(amount)", groupBy: ["period"], filters: { account_type: "opex" }, orderBy: "dim" });
    const monthly = revByMonth.map((r) => ({ period: r.period, revenue: r.value, opex: opexByMonth.find((o) => o.period === r.period)?.value || 0 }));
    const ccs = [...new Set(gl.filter((g) => g.account_type === "opex").map((g) => g.cost_center))];
    const ccRows = ccs.map((cc) => {
      const actual = measure(gl, "SUM(amount)", { cost_center: cc });
      const planAmt = planOverrides[cc] ?? plan.filter((p) => p.cost_center === cc).reduce((s, p) => s + p.plan_amount, 0);
      return { cc, actual, plan: planAmt, variance: planAmt - actual, variancePct: planAmt ? ((planAmt - actual) / planAmt) * 100 : 0 };
    }).sort((a, b) => b.actual - a.actual);
    const q = Math.min(3, Math.floor(monthly.length / 2)) || 1;
    const firstQ = monthly.slice(0, q).reduce((s, m) => s + m.revenue, 0) / q;
    const lastQ = monthly.slice(-q).reduce((s, m) => s + m.revenue, 0) / q;
    const growthPct = firstQ ? (lastQ / firstQ - 1) * (12 / Math.max(monthly.length - q, 1)) * 100 : 0;
    return { rev, opex, monthly, ccRows, growthPct };
}

export default function Pnl() {
  const tables = useData((s) => s.tables);
  const { planOverrides, setPlanOverride } = useDrivers();
  const openAt = useDrill((s) => s.openAt);
  const { rev, opex, monthly, ccRows, growthPct } = useMemo(() => computePnl(tables, planOverrides), [tables, planOverrides]);

  const marginPct = rev ? ((rev - opex) / rev) * 100 : 0;
  const r40 = ruleOf40(growthPct, marginPct);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--navy)" }}>Planning & P&L</h2>
        <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>Every figure rolls up from {(tables.fact_gl || []).length.toLocaleString()} GL journal lines. Plan figures are editable drivers.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard testId="kpi-revenue" label="Revenue (FY)" value={fmtMoney(rev)} sub="Recognized, from GL" module="pnl" seed={[{ field: "account_type", value: "revenue" }]} />
        <KpiCard testId="kpi-opex" label="OpEx (FY)" value={fmtMoney(opex)} sub="All cost centers" module="pnl" seed={[{ field: "account_type", value: "opex" }]} />
        <KpiCard testId="kpi-margin" label="Operating Margin" value={fmtPct(marginPct, 1)} status={marginPct >= 10 ? "pass" : marginPct >= 0 ? "watch" : "miss"} module="pnl" seed={[]} />
        <KpiCard testId="kpi-rule40" label="Rule of 40" value={fmtNum(r40, 0)} sub={`${fmtPct(growthPct, 0)} growth + ${fmtPct(marginPct, 0)} margin`} status={r40 >= 40 ? "pass" : "watch"} module="pnl" seed={[]} />
      </div>

      <div className="panel p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: "var(--slate)" }}>Revenue vs OpEx by month — click a bar to drill to cost centers → accounts → journal lines</div>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--slate)" }} />
              <YAxis tickFormatter={fmtMoney} tick={{ fontSize: 11, fill: "var(--slate)" }} width={64} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                <div className="rounded-md shadow-lg px-3 py-2 text-xs font-mono" style={{ background: "var(--navy)", color: "#fff" }}>
                  <div className="font-bold" style={{ color: "var(--cyan)" }}>{label}</div>
                  {payload.map((p) => <div key={p.name} style={{ color: "var(--teal)" }}>{p.name}: {fmtMoney(p.value)}</div>)}
                </div>) : null} />
              <Bar dataKey="revenue" name="Revenue" fill="var(--teal)" radius={[3, 3, 0, 0]} cursor="pointer"
                onClick={(d) => d && openAt("pnl", [{ field: "period", value: d.period }, { field: "account_type", value: "revenue" }])} />
              <Bar dataKey="opex" name="OpEx" fill="var(--navy3)" radius={[3, 3, 0, 0]} cursor="pointer"
                onClick={(d) => d && openAt("pnl", [{ field: "period", value: d.period }, { field: "account_type", value: "opex" }])} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="dtable" data-testid="pnl-table">
          <thead><tr><th>Cost Center</th><th className="text-right">Actual (fact ↓)</th><th className="text-right">Plan (driver ↑)</th><th className="text-right">Variance</th><th className="text-right">Var %</th></tr></thead>
          <tbody>
            {ccRows.map((r) => (
              <tr key={r.cc}>
                <td>
                  <Drillable module="pnl" seed={[{ field: "cost_center", value: r.cc }]} testId={`drill-cc-${r.cc.replace(/\s|&/g, "-")}`} className="inline-block">
                    <span className="fact-cell font-semibold pr-4">{r.cc}</span>
                  </Drillable>
                </td>
                <td className="text-right">
                  <Drillable module="pnl" seed={[{ field: "cost_center", value: r.cc }]} testId={`drill-cc-actual-${r.cc.replace(/\s|&/g, "-")}`} className="inline-block">
                    <span className="fact-cell pr-4">{fmtMoney(r.actual)}</span>
                  </Drillable>
                </td>
                <td className="text-right">
                  <DriverCell testId={`driver-plan-${r.cc.replace(/\s|&/g, "-")}`} value={r.plan} display={fmtMoney(r.plan)} onSave={(v) => setPlanOverride(r.cc, v)} />
                </td>
                <td className="text-right num font-bold" style={{ color: r.variance >= 0 ? "var(--good)" : "var(--bad)" }}>{fmtMoney(r.variance)}</td>
                <td className="text-right num" style={{ color: r.variance >= 0 ? "var(--good)" : "var(--bad)" }}>{fmtPct(r.variancePct, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
